import Button from '@components/buttons/Button/Button';
import Elliptical from '@components/spinners/Elliptical/Elliptical';
import { logger as sentryLogger } from '@sentry/react';
import React, { CSSProperties, useState } from 'react';

type PlayerState = 'idle' | 'loading' | 'playing' | 'fetchError' | 'audioError';

interface AudioPlayerProps {
    fetchAudioUrl: () => Promise<string>;
    testId?: string;
}

/**
 * AudioPlayer - Wrapper for the `audio` element which only fetches its audio file on user request.
 * 
 * Designed for lists such as the Aircall Archive UI where many audio files are on offer, but only a few will be
 * retrieved for a given use case, so we don't want to load them all or even pre-authorise their downloads.
 * The Play button stops click propagation, so it can be nested inside a clickable row without toggling its parent.
 * 
 * Using the fetchAudioUrl resolver allows for on-demand generation of a given file URL if needed. The component goes
 * from an idle state (showing the Play button) to a loading spinner while this method runs, then changes to render the
 * audio component itself once the URL is available.
 * 
 * Errors in fetching or playing the audio will be logged & transition to an error state. The Play button reappears for
 * retry, with a state-specific error message.
 * 
 * @param fetchAudioUrl A method to be called when the user clicks "Play", which should return a source URL for the file
 * @param testId Will be included in the `data-testid` property of the component's containing div
 */
const AudioPlayer = ({ fetchAudioUrl, testId }: AudioPlayerProps) => {
    const [state, setState] = useState<PlayerState>('idle');
    const [audioUrl, setAudioUrl] = useState<string>('');

    const handlePlay = (e: React.MouseEvent) => {
        e.stopPropagation();
        setState('loading');
        fetchAudioUrl()
            .then(url => {
                setAudioUrl(url);
                setState('playing');
            })
            .catch(() => {
                setState('fetchError');
            });
    };

    const handleAudioError = (e: React.SyntheticEvent<HTMLAudioElement>) => {
        const mediaError = e.currentTarget.error;
        sentryLogger.error('Audio playback error', {
            audioUrl,
            errorCode: mediaError?.code,
            errorMessage: mediaError?.message,
        });
        setAudioUrl('');
        setState('audioError');
    };

    const errorMessageStyle: CSSProperties = {
        color: '#D32F2F',
        fontSize: '12px',
        margin: '4px 0 0 0',
    };

    return (
        <div data-testid={testId} onClick={e => e.stopPropagation()}>
            {(state === 'idle' || state === 'fetchError' || state === 'audioError') && (
                <Button
                    label="Play"
                    onClickFunc={handlePlay}
                    testId="play-button"
                />
            )}

            {state === 'loading' && (
                <span data-testid="loading-spinner"><Elliptical /></span>
            )}

            {state === 'playing' && (
                <audio
                    data-testid="audio-element"
                    src={audioUrl}
                    controls
                    autoPlay
                    onError={handleAudioError}
                />
            )}

            {state === 'fetchError' && (
                <p style={errorMessageStyle}>Unable to play — please try again</p>
            )}

            {state === 'audioError' && (
                <p style={errorMessageStyle}>Recording could not be played</p>
            )}
        </div>
    );
};

export default AudioPlayer;
